import { useState } from 'react';
// @mui
import Typography from '@mui/material/Typography';
// components
import {
  IEditForm,
  CasEditForm,
  CasRHFMultilineTextField,
  CasDisplayField,
  DependentSelect,
  CasStackRow,
} from 'src/compass/shared/components';
import {
  CasAsOfContainer,
  EntityInfoCMEAccounts,
  useDefaultAsOfValues,
} from 'src/compass/application/commissions';
import { CasRHFRiskManagerSelect } from 'src/compass/application/general/components/CasRiskManagerSelect';

// schema
import { formatDate, DateFormat } from 'src/compass/shared/util/casMoment';

import AccountAgreementAndPercentFields from './AccountAgreementAndPercentFields';
import { CMEAccountsSchema } from './CMEAccountsSchema';
import { RHFAdditClearingSelect } from '../AdditClearingSelect';
import SourceEmployeeAndTypeFields from '../SourceEmployeeAndTypeFields';

// ----------------------------------------------------------------------

export default function CMEAccountsForm({ row, onSaved }: IEditForm) {
  const { id } = row || {};

  const [asofReadonly, setAsofReadonly] = useState(false);

  const [servicingBrokerId, setServicingBrokerId] = useState();

  const defaultValues = useDefaultAsOfValues(row);

  return (
    <CasEditForm
      defaultValues={defaultValues}
      onSaved={onSaved}
      entity={EntityInfoCMEAccounts.entity}
      schema={CMEAccountsSchema}
    >
      <CasStackRow>
        <CasDisplayField label="Name" value={row?.name} />
        <CasDisplayField label="Account" value={row?.account} />
        <CasDisplayField label="Office-Sales" value={row?.officeSalesCode} />
        <CasDisplayField label="Date Open" value={formatDate(row?.opened, DateFormat.MDY)} />
        <CasDisplayField label="Handoff" value={formatDate(row?.handoffAsOfDate, DateFormat.MDY)} />
      </CasStackRow>
      <CasStackRow>
        <CasDisplayField label="Customer" value={row?.customer} />
      </CasStackRow>
      <CasRHFMultilineTextField name="notes" label="Notes" />

      <CasAsOfContainer
        entityTitle='CME Account AsOf'
        entityId={id}
        deleteChildEntity="Cme/Account/AsOf"
        initialAsOfList={row?.asOfs}
        initialAsOf={defaultValues.asOf}
        readOnly={asofReadonly}
        includeCustomOption={false}
        setReadOnly={setAsofReadonly}
      >
        <CasRHFRiskManagerSelect
          name="asOf.brokerId"
          label="Relationship Owner"
          onChange={(_, value) => setServicingBrokerId(value)}
          disabled={asofReadonly}
        />
        <DependentSelect
          entity="Cme/CommSchedule/Select"
          primaryDependentValue="asOf.brokerId"
          additionalDependentValue={['asOf.asOfDate']}
          param="servicingBrokerId"
          name="asOf.cmeCommScheduleId"
          label="Commission Schedule"
          disabled={!servicingBrokerId || asofReadonly}
        />
        <SourceEmployeeAndTypeFields asofReadonly={asofReadonly} />
        <RHFAdditClearingSelect
          name="asOf.cmeAdditClearingId"
          label="Additional Clearing"
          disabled={!servicingBrokerId || asofReadonly}
        />
        <AccountAgreementAndPercentFields asofReadonly={asofReadonly}/>
      </CasAsOfContainer>
      <Typography
        variant="caption"
        sx={{
          fontStyle: 'italic',
          color: 'text.disabled',
        }}
      >
        AccountId:{id}
      </Typography>
    </CasEditForm>
  );
}
